class TabbedProductBlock extends HTMLElement {
  constructor() {
    super();
    this.tabs = this.querySelectorAll(".tabbed-product-block__tab");
    this.panels = this.querySelectorAll(".tabbed-product-block__panel");
    this.activeTab = null;
    this.defaultIndex = parseInt(this.dataset.defaultIndex || "1", 10);

    this.init();
  }

  init() {
    const defaultTab = this.dataset.defaultTab || "subscribe";
    this.bindEvents();
    this.switchTab(defaultTab);
    this.initialized = true;
    this.bindProductSelection();
    this.selectFirstProductInActiveTab(this.defaultIndex);
  }

  connectedCallback() {
    // The plan-switch link lives outside this element (see syncPlanLinks), so
    // its label can only be settled once both are in the document.
    this.syncPlanLinks();
  }

  bindEvents() {
    this.tabs.forEach((tab) => {
      tab.addEventListener("click", (e) => {
        e.preventDefault();
        const tabName = tab.getAttribute("data-tab");
        this.switchTab(tabName, this.topOptionIndex(tabName));
      });

      tab.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          const tabName = tab.getAttribute("data-tab");
          this.switchTab(tabName, this.topOptionIndex(tabName));
        }
      });
    });
  }

  /**
   * DOM index (1-based) of the option shown at the TOP of a tab's list.
   *
   * Normally that is the first one, but the subscribe panel is laid out
   * column-reverse when .reverse-subscribe-order is set, so its top card is
   * the LAST in the DOM. Picking a tab by hand should land on whatever the
   * customer sees first, so this resolves position rather than assuming
   * DOM order matches visual order.
   */
  topOptionIndex(tabName) {
    if (tabName !== "subscribe") return 1;
    if (!this.classList.contains("reverse-subscribe-order")) return 1;

    const panel = this.querySelector(
      '.tabbed-product-block__panel[data-tab="subscribe"]'
    );
    const count = panel ? this.optionsIn(panel).length : 0;
    return count > 0 ? count : 1;
  }

  /**
   * The option cards in a panel: the <li>s of its product list and nothing
   * deeper. A bare querySelectorAll("li") would also pick up any list markup
   * inside a card, and the index arithmetic in selectFirstProductInActiveTab
   * and topOptionIndex counts on one entry per option.
   */
  optionsIn(panel) {
    return panel.querySelectorAll(
      ":scope > .tabbed-product-block__products > .tabbed-product-block__product-list > li"
    );
  }

  switchTab(tabName, index = 1) {
    if (tabName === this.activeTab) return;

    this.tabs.forEach((tab) => {
      const isActive = tab.getAttribute("data-tab") === tabName;
      tab.classList.toggle("tabbed-product-block__tab--active", isActive);
      tab.setAttribute("aria-selected", isActive);
    });

    this.panels.forEach((panel) => {
      const isActive = panel.getAttribute("data-tab") === tabName;
      panel.classList.toggle("tabbed-product-block__panel--active", isActive);
    });

    this.activeTab = tabName;

    if (this.initialized) {
      this.selectFirstProductInActiveTab(index);
    }

    this.syncPlanLinks();

    // bubbles so listeners on document (custom-product-button.js) hear it; a
    // non-bubbling event stops at this element.
    this.dispatchEvent(
      new CustomEvent("tabChanged", {
        bubbles: true,
        detail: { activeTab: tabName },
      })
    );
  }

  setActiveTab(tabName) {
    this.switchTab(tabName);
  }

  getActiveTab() {
    return this.activeTab;
  }

  /**
   * The plan-switch link under the add-to-basket button reads as whichever
   * plan is NOT showing, so it always offers the other one: "One Time
   * Purchase" while the subscribe panel is up, "Subscribe & Save" while the
   * one-time panel is. It sits outside this element (in the custom_button
   * block of custom-collection-block.liquid), hence the walk up to the
   * section. Labels come from the link's own data attributes so the theme
   * editor settings reach it.
   */
  syncPlanLinks() {
    if (!this.isConnected) return;
    const scope = this.closest(".shopify-section") || document;
    const onOneTime = this.activeTab === "one-time";
    scope.querySelectorAll("[data-one-time-purchase]").forEach((link) => {
      const label = onOneTime
        ? link.dataset.labelSubscribe
        : link.dataset.labelOneTime;
      if (label) link.textContent = label;
      link.setAttribute("data-shows", onOneTime ? "subscribe" : "one-time");
    });
  }

  selectFirstProductInActiveTab(index = 1) {
    const activePanel = this.querySelector(
      ".tabbed-product-block__panel--active"
    );
    if (activePanel) {
      const allProducts = this.optionsIn(activePanel);
      if (allProducts.length > 0) {
        allProducts.forEach((product) => {
          product.classList.remove("active-product");
        });

        const targetIdx = Math.max(0, Math.min(index - 1, allProducts.length - 1));
        allProducts[targetIdx].classList.add("active-product");
      }
    }
  }

  /**
   * Select the option in the active panel that carries this variant id.
   * Returns false, and changes nothing, if the panel has no such option.
   */
  selectProductByVariantId(variantId) {
    const activePanel = this.querySelector(
      ".tabbed-product-block__panel--active"
    );
    if (!activePanel || !variantId) return false;
    const allProducts = this.optionsIn(activePanel);
    const target = Array.from(allProducts).find(
      (product) => product.dataset.variantId === String(variantId)
    );
    if (!target) return false;
    allProducts.forEach((product) => {
      product.classList.remove("active-product");
    });
    target.classList.add("active-product");
    return true;
  }

  bindProductSelection() {
    const productItems = [];
    this.panels.forEach((panel) => {
      this.optionsIn(panel).forEach((item) => productItems.push(item));
    });

    productItems.forEach((item) => {
      item.addEventListener("click", (e) => {
        if (
          e.target.closest(".quick-add") ||
          e.target.closest("select") ||
          e.target.closest("input") ||
          e.target.closest("button") ||
          e.target.closest(".product-form") ||
          e.target.closest(".card-product")
        ) {
          return;
        }

        if (e.target !== item && !item.contains(e.target)) {
          return;
        }

        const currentPanel = item.closest(".tabbed-product-block__panel");
        const allItemsInPanel = this.optionsIn(currentPanel);
        allItemsInPanel.forEach((panelItem) => {
          panelItem.classList.remove("active-product");
        });

        item.classList.add("active-product");
      });
    });
  }
}

customElements.define("tabbed-product-block", TabbedProductBlock);

/**
 * Plan-switch link, rendered under the add-to-basket button on the PDP (see
 * the custom_button block in custom-collection-block.liquid).
 *
 * The PDP never shows the tab strip: .tabs-hidden stays on the element for
 * the life of the page, and this link is the only way between the two plans.
 * Pressing it swaps to the panel it names (its label is kept in step by
 * syncPlanLinks). Coming back to subscribe lands on the page's default option
 * (data-default-index) rather than the first in the DOM, so the customer sees
 * the same card they started on; one-time lands on its first option.
 *
 * It does not add to the cart; the add-to-basket button above does that, and
 * follows whatever this selects.
 *
 * Delegated from document because the link sits outside <tabbed-product-block>
 * and custom-product-button.js re-renders that corner of the DOM.
 */
document.addEventListener("click", (e) => {
  const trigger =
    e.target.closest && e.target.closest("[data-one-time-purchase]");
  if (!trigger) return;

  const scope = trigger.closest(".shopify-section") || document;
  const block = scope.querySelector("tabbed-product-block");
  // No element, or it has not upgraded yet: leave the link alone so a second
  // press can still work.
  if (!block || typeof block.switchTab !== "function") return;

  const next = block.getActiveTab() === "one-time" ? "subscribe" : "one-time";
  block.switchTab(next, next === "subscribe" ? block.defaultIndex : 1);
});

/**
 * "Switch to Subscribe & Save" on a one-time card (the .card-missed block in
 * snippets/custom-card-variant.liquid). Same job as the link above, but it
 * knows which subscription it is the counterpart of: data-target-variant is
 * the subscription variant with the same box count, so a shopper looking at
 * the 3-box one-time price lands on the 3-month plan, not the monthly one.
 * If that variant is not in the panel, the default selection stands.
 */
document.addEventListener("click", (e) => {
  const trigger = e.target.closest && e.target.closest("[data-switch-plan]");
  if (!trigger) return;

  const scope = trigger.closest(".shopify-section") || document;
  const block = scope.querySelector("tabbed-product-block");
  if (!block || typeof block.switchTab !== "function") return;

  const tab = trigger.dataset.switchPlan || "subscribe";
  block.switchTab(tab, tab === "subscribe" ? block.defaultIndex : 1);
  block.selectProductByVariantId(trigger.dataset.targetVariant);
});

/* ---------------------------------------------------------------------------
   Enlarged gift images (client, 2026-09-09). The pictures on the plan cards are
   58-80px, too small to make out what the gift actually is; clicking one opens
   it at 1200px wide in a dialog.

   The trigger is a <button data-gift-zoom>, which is what stops the click from
   also selecting that plan — the card handler above returns early on
   e.target.closest("button"). Keep it a button.

   One dialog is built lazily and reused. Click delegation is on document so it
   keeps working when a panel re-renders on a tab switch.
   --------------------------------------------------------------------------- */
(function () {
  let box, imgEl, titleEl, capEl, closeEl, lastTrigger;

  function build() {
    if (box) return;
    box = document.createElement("div");
    box.className = "sis-giftbox";
    box.hidden = true;
    box.setAttribute("role", "dialog");
    box.setAttribute("aria-modal", "true");
    box.innerHTML =
      '<div class="sis-giftbox__panel">' +
      '<button type="button" class="sis-giftbox__close" data-giftbox-close aria-label="Close">&times;</button>' +
      '<img class="sis-giftbox__img" alt="">' +
      '<p class="sis-giftbox__title"></p>' +
      '<p class="sis-giftbox__caption"></p>' +
      "</div>";
    document.body.appendChild(box);
    imgEl = box.querySelector(".sis-giftbox__img");
    titleEl = box.querySelector(".sis-giftbox__title");
    capEl = box.querySelector(".sis-giftbox__caption");
    closeEl = box.querySelector(".sis-giftbox__close");
    box.addEventListener("click", (e) => {
      // Anything outside the panel closes, rather than only the backdrop element:
      // the backdrop once collapsed to 0x0 and took click-outside down with it,
      // and this does not depend on its geometry being right.
      if (e.target.closest("[data-giftbox-close]") || !e.target.closest(".sis-giftbox__panel")) {
        closeBox();
      }
    });
  }

  function openBox(trigger) {
    build();
    lastTrigger = trigger;
    const d = trigger.dataset;
    imgEl.src = d.zoomSrc || "";
    imgEl.alt = d.zoomAlt || "";
    titleEl.textContent = d.zoomTitle || "";
    titleEl.hidden = !d.zoomTitle;
    // The worth reads as a struck-through price, as on the card (2026-09-16); the
    // hidden "Worth" keeps it meaningful to screen readers.
    capEl.textContent = "";
    if (d.zoomCaption) {
      const hidden = document.createElement("span");
      hidden.className = "visually-hidden";
      hidden.textContent = "Worth ";
      const struck = document.createElement("s");
      struck.textContent = d.zoomCaption;
      capEl.append(hidden, struck);
    }
    capEl.hidden = !d.zoomCaption;
    box.setAttribute("aria-label", d.zoomTitle || "Gift image");
    box.hidden = false;
    closeEl.focus();
  }

  function closeBox() {
    if (!box || box.hidden) return;
    box.hidden = true;
    if (lastTrigger) {
      lastTrigger.focus();
      lastTrigger = null;
    }
  }

  document.addEventListener("click", (e) => {
    const trigger = e.target.closest && e.target.closest("[data-gift-zoom]");
    if (!trigger) return;
    e.preventDefault();
    openBox(trigger);
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeBox();
  });
})();
